import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { ArrowUpCircle, CheckCircle2, XCircle, Clock, AlertCircle, FileText, DollarSign } from 'lucide-react';
import { projectId } from '../utils/supabase/info';
import { formatPrice } from '../utils/billingAdmin';
import type { UpgradeRequest } from '../types/billing-admin';
import type { User } from '../types';

interface UpgradeRequestsListProps {
  firmId: string;
  currentUser: User;
  accessToken: string;
  autoRefresh?: boolean;
}

export function UpgradeRequestsList({
  firmId,
  currentUser,
  accessToken,
  autoRefresh = false,
}: UpgradeRequestsListProps) {
  const [requests, setRequests] = useState<UpgradeRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedRequest, setSelectedRequest] = useState<UpgradeRequest | null>(null);
  const [showDenyDialog, setShowDenyDialog] = useState(false);
  const [denialReason, setDenialReason] = useState('');
  const [processing, setProcessing] = useState(false);

  const isBillingAdmin = currentUser.isBillingAdmin;

  useEffect(() => {
    loadRequests();
    
    if (autoRefresh) {
      const interval = setInterval(loadRequests, 30000); // Refresh every 30s
      return () => clearInterval(interval);
    }
  }, [firmId]);

  const loadRequests = async () => {
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/upgrade-requests/${firmId}`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to load upgrade requests');
      }

      setRequests(data.requests || []);
    } catch (err: any) {
      console.error('Error loading upgrade requests:', err);
      setError(err.message || 'Failed to load upgrade requests');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (request: UpgradeRequest) => {
    setProcessing(true);
    setError('');

    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/upgrade-requests/${request.id}/approve`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to approve request');
      }

      await loadRequests();
    } catch (err: any) {
      console.error('Error approving request:', err);
      setError(err.message || 'Failed to approve request');
    } finally {
      setProcessing(false);
    }
  };

  const handleDeny = async () => {
    if (!selectedRequest || !denialReason.trim()) {
      setError('Please provide a reason for denial');
      return;
    }

    setProcessing(true);
    setError('');

    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/upgrade-requests/${selectedRequest.id}/deny`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            denialReason: denialReason.trim(),
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to deny request');
      }

      setShowDenyDialog(false);
      setSelectedRequest(null);
      setDenialReason('');
      await loadRequests();
    } catch (err: any) {
      console.error('Error denying request:', err);
      setError(err.message || 'Failed to deny request');
    } finally {
      setProcessing(false);
    }
  };

  const getStatusBadge = (status: UpgradeRequest['status']) => {
    switch (status) {
      case 'pending':
        return (
          <Badge variant="outline" className="gap-1">
            <Clock className="h-3 w-3" />
            Pending
          </Badge>
        );
      case 'approved':
        return (
          <Badge variant="default" className="gap-1 bg-green-600">
            <CheckCircle2 className="h-3 w-3" />
            Approved
          </Badge>
        );
      case 'denied':
        return (
          <Badge variant="destructive" className="gap-1">
            <XCircle className="h-3 w-3" />
            Denied
          </Badge>
        );
      case 'cancelled':
        return (
          <Badge variant="secondary" className="gap-1">
            <XCircle className="h-3 w-3" />
            Cancelled
          </Badge>
        );
    }
  };

  const getPlanName = (plan: string): string => {
    return plan.charAt(0).toUpperCase() + plan.slice(1);
  };

  const getRequestDescription = (request: UpgradeRequest): string => {
    if (request.requestType === 'plan_upgrade') {
      return `Upgrade from ${getPlanName(request.fromPlan!)} to ${getPlanName(request.toPlan!)}`;
    } else {
      return `Add ${request.companySlotPacks} company slot pack(s)`;
    }
  };

  const pendingRequests = requests.filter(r => r.status === 'pending');
  const completedRequests = requests.filter(r => r.status !== 'pending');

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ArrowUpCircle className="h-5 w-5" />
            Upgrade Requests
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-muted-foreground">
            Loading upgrade requests...
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ArrowUpCircle className="h-5 w-5" />
            Upgrade Requests
            {pendingRequests.length > 0 && (
              <Badge variant="destructive">{pendingRequests.length}</Badge>
            )}
          </CardTitle>
          <CardDescription>
            {isBillingAdmin 
              ? 'Review and approve upgrade requests from your team'
              : 'View your upgrade request history'
            }
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Pending Requests */}
          {pendingRequests.length > 0 && (
            <div className="space-y-3">
              <h3 className="font-medium">Pending Approval</h3>
              <div className="border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Request</TableHead>
                      <TableHead>Requested By</TableHead>
                      <TableHead>Cost Impact</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Status</TableHead>
                      {isBillingAdmin && <TableHead>Actions</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pendingRequests.map((request) => (
                      <TableRow key={request.id}>
                        <TableCell>
                          <div className="space-y-1">
                            <p className="font-medium">{getRequestDescription(request)}</p>
                            <p className="text-muted-foreground line-clamp-1">{request.reason}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            <p>{request.requestedByName}</p>
                            <p className="text-muted-foreground">{request.requestedByEmail}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <DollarSign className="h-4 w-4 text-muted-foreground" />
                            <span className="font-medium">{formatPrice(request.costImpact)}/mo</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          {new Date(request.requestedAt).toLocaleDateString()}
                        </TableCell>
                        <TableCell>{getStatusBadge(request.status)}</TableCell>
                        {isBillingAdmin && (
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Button
                                size="sm"
                                onClick={() => handleApprove(request)}
                                disabled={processing}
                                className="bg-green-600 hover:bg-green-700"
                              >
                                <CheckCircle2 className="h-4 w-4 mr-1" />
                                Approve
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setSelectedRequest(request);
                                  setShowDenyDialog(true);
                                }}
                                disabled={processing}
                              >
                                <XCircle className="h-4 w-4 mr-1" />
                                Deny
                              </Button>
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}

          {/* Completed Requests */}
          {completedRequests.length > 0 && (
            <div className="space-y-3">
              <h3 className="font-medium">Request History</h3>
              <div className="border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Request</TableHead>
                      <TableHead>Requested By</TableHead>
                      <TableHead>Cost Impact</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Reviewed By</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {completedRequests.map((request) => (
                      <TableRow key={request.id}>
                        <TableCell>
                          <div className="space-y-1">
                            <p className="font-medium">{getRequestDescription(request)}</p>
                            <p className="text-muted-foreground line-clamp-1">{request.reason}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            <p>{request.requestedByName}</p>
                            <p className="text-muted-foreground">{request.requestedByEmail}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <DollarSign className="h-4 w-4 text-muted-foreground" />
                            <span>{formatPrice(request.costImpact)}/mo</span>
                          </div>
                        </TableCell>
                        <TableCell>{getStatusBadge(request.status)}</TableCell>
                        <TableCell>
                          {request.reviewedByName ? (
                            <div className="space-y-1">
                              <p>{request.reviewedByName}</p>
                              <p className="text-muted-foreground">
                                {new Date(request.reviewedAt!).toLocaleDateString()}
                              </p>
                              {request.denialReason && (
                                <p className="text-muted-foreground italic">
                                  "{request.denialReason}"
                                </p>
                              )}
                            </div>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}

          {requests.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No upgrade requests yet</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Deny Dialog */}
      <Dialog open={showDenyDialog} onOpenChange={setShowDenyDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <XCircle className="h-5 w-5 text-destructive" />
              Deny Upgrade Request
            </DialogTitle>
            <DialogDescription>
              Please provide a reason for denying this request. The requester will be notified.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {selectedRequest && (
              <Alert>
                <FileText className="h-4 w-4" />
                <AlertDescription>
                  <div className="space-y-1">
                    <p className="font-medium">{getRequestDescription(selectedRequest)}</p>
                    <p className="text-muted-foreground">
                      Requested by: {selectedRequest.requestedByName}
                    </p>
                    <p className="text-muted-foreground">
                      Reason: "{selectedRequest.reason}"
                    </p>
                  </div>
                </AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="denialReason">Reason for Denial *</Label>
              <Textarea
                id="denialReason"
                placeholder="Please explain why this upgrade request cannot be approved at this time..."
                value={denialReason}
                onChange={(e) => setDenialReason(e.target.value)}
                required
                rows={4}
                maxLength={500}
              />
              <p className="text-muted-foreground">
                {denialReason.length}/500 characters
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowDenyDialog(false);
                setSelectedRequest(null);
                setDenialReason('');
                setError('');
              }}
              disabled={processing}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeny}
              disabled={processing || !denialReason.trim()}
            >
              {processing ? 'Denying...' : 'Confirm Denial'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
